const AUTO_DISMISS_MS = 10000
const MIN_DISPLAY_MS = 1500

export enum ToastVariant {
    LOADING = 'loading',
    SUCCESS = 'success',
    ERROR = 'error',
}

export interface ToastOptions {
    message: string
    variant?: ToastVariant
    actionText?: string
    onAction?: () => void
}

type Listener = (current: ToastOptions | null) => void

let current: ToastOptions | null = null
let shownAt: number | null = null
let timerId: ReturnType<typeof setTimeout> | null = null
let pending: { options: ToastOptions; applyAt: number } | null = null
let pendingTimerId: ReturnType<typeof setTimeout> | null = null
const listeners: Set<Listener> = new Set()

const clearTimer = (): void => {
    if (timerId !== null) {
        clearTimeout(timerId)
        timerId = null
    }
}

const clearPendingTimer = (): void => {
    if (pendingTimerId !== null) {
        clearTimeout(pendingTimerId)
        pendingTimerId = null
    }
    pending = null
}

const applyToast = (options: ToastOptions): void => {
    clearTimer()
    current = { ...options }
    shownAt = Date.now()
    notify()

    const variant = options.variant ?? ToastVariant.SUCCESS
    const hasAction = options.actionText != null && options.onAction != null
    const shouldAutoDismiss = variant !== ToastVariant.LOADING && !hasAction

    if (shouldAutoDismiss) {
        const snapshot = current
        timerId = setTimeout(() => {
            timerId = null
            if (current === snapshot) {
                current = null
                shownAt = null
                notify()
            }
        }, AUTO_DISMISS_MS)
    }
}

const notify = (): void => {
    listeners.forEach((listener) => listener(current))
}

export const subscribe = (listener: Listener): (() => void) => {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

export const getCurrent = (): ToastOptions | null => {
    return current
}

export const show = (options: ToastOptions): void => {
    const now = Date.now()
    const elapsedTime = shownAt != null ? now - shownAt : MIN_DISPLAY_MS

    if (current != null && elapsedTime < MIN_DISPLAY_MS) {
        clearPendingTimer()
        const applyAt = shownAt! + MIN_DISPLAY_MS
        pending = { options, applyAt }
        pendingTimerId = setTimeout(() => {
            pendingTimerId = null
            const toApply = pending
            pending = null
            if (toApply != null) applyToast(toApply.options)
        }, applyAt - now)
        return
    }

    clearPendingTimer()
    applyToast(options)
}

export const dismiss = (): void => {
    clearTimer()
    clearPendingTimer()
    current = null
    shownAt = null
    notify()
}

export const toast = { show, dismiss, subscribe, getCurrent }
